import {configureStore} from "@reduxjs/toolkit"
import {
    FLUSH,
    PAUSE,
    PERSIST,
    PURGE,
    REGISTER,
    REHYDRATE,
    createMigrate,
    createTransform,
    persistCombineReducers,
    persistStore
} from "redux-persist"
import storage from "redux-persist/lib/storage"
import {useDispatch as useDefaultDispatch} from "react-redux"
import type {PersistedState} from "redux-persist/es/types"
import auth from "./auth/authSlice"
import app, {getInitialSelectedBranchId} from "./app/appSlice"
import {branchApi} from "./branch/branchApi"
import {shippingApi} from "./shipping/shippingApi"
import {courierApi} from "./courier/courierApi"
import {reviewCategoryTagApi} from "./review-category-tag/reviewCategoryTagApi"
import {reviewCategoryApi} from "./review-category/reviewCategoryApi"
import {reviewsApi} from "./review/reviewApi"
import {orderApi} from "./order/orderApi"
import {menuApi} from "./menu/menuApi"
import {bannerApi} from "./banner/bannerApi"
import {storyApi} from "./story/storyApi"
import {dashboardApi} from "./dashboard/dashboardApi"
import {userApi} from "./user/userApi"
import {paymentTypeApi} from "./payment-type/paymentTypeApi"
import {telegramNotificationApi} from "./telegram-notification/telegramNotificationApi"
import {promotionApi} from "./promotion/promotionApi"
import {customerApi} from "./customer/customerApi"
import {geoApi} from "./delivery-heatmap/geoApi"
import {reportApi} from "./reports/reportApi"

export const apiSlices = [
    branchApi,
    shippingApi,
    courierApi,
    reviewCategoryTagApi,
    reviewCategoryApi,
    reviewsApi,
    orderApi,
    menuApi,
    bannerApi,
    storyApi,
    dashboardApi,
    userApi,
    paymentTypeApi,
    telegramNotificationApi,
    promotionApi,
    customerApi,
    geoApi,
    reportApi
] as const

const appPersistTransform = createTransform(
    (inboundState: {theme?: "light" | "dark"}) => ({
        theme: inboundState?.theme || "dark"
    }),
    (outboundState: {theme?: "light" | "dark"}) => ({
        theme: outboundState?.theme || "dark",
        selectedBranchId: getInitialSelectedBranchId()
    }),
    {whitelist: ["app"]}
)

const persistConfig = {
    key: "root",
    storage,
    version: 3,
    whitelist: ["auth", "app"],
    transforms: [appPersistTransform],
    migrate: createMigrate({
        2: (state: PersistedState) => {
            if (!state) return state
            const legacyState = state as PersistedState & {
                auth?: unknown
                app?: {theme?: "light" | "dark"}
            }

            return {
                auth: legacyState.auth,
                app: legacyState.app ? {theme: legacyState.app.theme || "dark"} : undefined,
                _persist: state._persist
            }
        },
        3: (state: PersistedState) => {
            if (!state) return state
            const legacyState = state as PersistedState & {
                auth?: unknown
                app?: {theme?: "light" | "dark"}
            }

            return {
                auth: legacyState.auth,
                app: legacyState.app ? {theme: legacyState.app.theme || "dark"} : {theme: "dark"},
                _persist: state._persist
            }
        }
    }, {debug: false})
}

const persistedCombineReducers = persistCombineReducers(persistConfig, {
    auth,
    app,
    [reviewsApi.reducerPath]: reviewsApi.reducer,
    [branchApi.reducerPath]: branchApi.reducer,
    [shippingApi.reducerPath]: shippingApi.reducer,
    [courierApi.reducerPath]: courierApi.reducer,
    [reviewCategoryTagApi.reducerPath]: reviewCategoryTagApi.reducer,
    [reviewCategoryApi.reducerPath]: reviewCategoryApi.reducer,
    [orderApi.reducerPath]: orderApi.reducer,
    [menuApi.reducerPath]: menuApi.reducer,
    [bannerApi.reducerPath]: bannerApi.reducer,
    [storyApi.reducerPath]: storyApi.reducer,
    [dashboardApi.reducerPath]: dashboardApi.reducer,
    [userApi.reducerPath]: userApi.reducer,
    [paymentTypeApi.reducerPath]: paymentTypeApi.reducer,
    [telegramNotificationApi.reducerPath]: telegramNotificationApi.reducer,
    [promotionApi.reducerPath]: promotionApi.reducer,
    [customerApi.reducerPath]: customerApi.reducer,
    [geoApi.reducerPath]: geoApi.reducer,
    [reportApi.reducerPath]: reportApi.reducer
})

export const store = configureStore({
    reducer: persistedCombineReducers,
    middleware: getDefaultMiddleware =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
            }
        })
            .concat(branchApi.middleware)
            .concat(shippingApi.middleware)
            .concat(courierApi.middleware)
            .concat(reviewCategoryTagApi.middleware)
            .concat(reviewCategoryApi.middleware)
            .concat(reviewsApi.middleware)
            .concat(orderApi.middleware)
            .concat(menuApi.middleware)
            .concat(bannerApi.middleware)
            .concat(storyApi.middleware)
            .concat(dashboardApi.middleware)
            .concat(userApi.middleware)
            .concat(paymentTypeApi.middleware)
            .concat(telegramNotificationApi.middleware)
            .concat(promotionApi.middleware)
            .concat(customerApi.middleware)
            .concat(geoApi.middleware)
            .concat(reportApi.middleware)
})

export const persistor = persistStore(store)

export type StoreState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

export interface ThunkProps {
    dispatch: AppDispatch
    state: StoreState
    extra?: unknown
    rejectValue?: unknown
}

export const useDispatch = () => useDefaultDispatch<AppDispatch>()
